import { useState, useEffect, useCallback, useRef } from 'react';
import { useIonRouter } from '@ionic/react';
import { DataTable } from '../shared/DataTable';
import { searchBusinesses } from '../../services/businessService';
import { AssociateClientModal } from './AssociateClientModal';
import { InviteTeamAdminModal } from './InviteTeamAdminModal';
import { ManageSeatsModal } from './ManageSeatsModal';
import { InviteTeamOwnerModal } from './InviteTeamOwnerModal';
import { useRefresh } from '../../contexts/RefreshContext';
import type { Business } from '../../types/business';
import type { Column } from '../shared/DataTable';

const columns: Column<Business>[] = [
  {
    key: 'client',
    label: 'Client',
    render: (item) => item.client?.name ?? '—',
  },
  {
    key: 'logoUrl',
    label: '',
    render: (item) =>
      item.logoUrl ? (
        <img src={item.logoUrl} alt="" className="max-w-[80px] h-9 rounded object-contain" />
      ) : (
        <div className="w-9 h-9 rounded bg-gray-200 flex items-center justify-center">
          <span className="text-[8px] text-gray-400 font-medium select-none">LOGO</span>
        </div>
      ),
  },
  { key: 'name', label: 'Name' },
  {
    key: 'owner',
    label: 'Owner',
    render: (item) => item.ownerDisplayName || item.owner ? (
      <div>
        <div className="font-medium text-gray-900">{item.ownerDisplayName}</div>
        <div className="text-xs text-gray-500">{item.owner}</div>
      </div>
    ) : '—',
  },
  {
    key: 'activePlan',
    label: 'LMS Seats',
    render: (item) => item.activePlan?.totalSeats ?? '—',
  },
];

function RowMenu({ item, onAssociateClient, onInviteAdmin, onManageSeats }: {
  item: Business;
  onAssociateClient: () => void;
  onInviteAdmin: () => void;
  onManageSeats: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const showInviteAdmin = !!item.propertyId;
  const showManageSeats = !item.propertyId;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
          {!item.clientId && (
            <button
              onClick={() => { setOpen(false); onAssociateClient(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Associate to Client
            </button>
          )}
          {showInviteAdmin && (
            <button
              onClick={() => { setOpen(false); onInviteAdmin(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Invite Team Admin
            </button>
          )}
          {showManageSeats && (
            <button
              onClick={() => { setOpen(false); onManageSeats(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Manage LMS Seats
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface TeamListProps {
  title?: string;
  clientId?: string;
  propertyId?: string;
  primaryBusinessId?: string;
}

export function TeamList({ title = 'Teams', clientId, propertyId, primaryBusinessId }: TeamListProps) {
  const router = useIonRouter();

  const [data, setData] = useState<Business[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [associateClientBusinessId, setAssociateClientBusinessId] = useState<string | null>(null);
  const [inviteAdminBusinessId, setInviteAdminBusinessId] = useState<string | null>(null);
  const [manageSeatsBusinessId, setManageSeatsBusinessId] = useState<string | null>(null);
  const [showInviteOwnerModal, setShowInviteOwnerModal] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter: Record<string, unknown> = {};
      if (clientId) filter.clientId = clientId;
      if (propertyId) filter.propertyId = propertyId;
      const results = await searchBusinesses(filter, { page, pageSize, orderBy: 'updated', order: 'desc', expand: 'client' });
      // Sort primary team to top
      if (primaryBusinessId) {
        results.sort((a, b) => {
          if (a.id === primaryBusinessId) return -1;
          if (b.id === primaryBusinessId) return 1;
          return 0;
        });
      }
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, clientId, propertyId, primaryBusinessId]);

  useEffect(() => { fetch(); }, [fetch]);

  const refreshPath = `/teams-${clientId || propertyId || 'all'}`;
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler(refreshPath, fetch);
    return () => unregisterRefreshHandler(refreshPath);
  }, [fetch, refreshPath, registerRefreshHandler, unregisterRefreshHandler]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <DataTable
        title={title}
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => setShowInviteOwnerModal(true)}
        rowClassName={primaryBusinessId ? (item) => item.id === primaryBusinessId ? 'bg-blue-50' : '' : undefined}
        actions={(item) => (
          <button
            onClick={() => router.push(`/profiles?businessId=${item.id}${clientId ? `&clientId=${clientId}` : ''}`, 'none')}
            className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
          >
            Users
          </button>
        )}
        rowMenu={(item) => (
          <RowMenu
            item={item}
            onAssociateClient={() => setAssociateClientBusinessId(item.id)}
            onInviteAdmin={() => setInviteAdminBusinessId(item.id)}
            onManageSeats={() => setManageSeatsBusinessId(item.id)}
          />
        )}
      />

      {showInviteOwnerModal && (
        <InviteTeamOwnerModal
          clientId={clientId}
          onClose={() => setShowInviteOwnerModal(false)}
          onSuccess={() => { setShowInviteOwnerModal(false); fetch(); }}
        />
      )}

      {associateClientBusinessId && (
        <AssociateClientModal
          businessId={associateClientBusinessId}
          onClose={() => setAssociateClientBusinessId(null)}
          onSuccess={async () => {
            setAssociateClientBusinessId(null);
            await fetch();
          }}
        />
      )}

      {inviteAdminBusinessId && (
        <InviteTeamAdminModal
          businessId={inviteAdminBusinessId}
          onClose={() => setInviteAdminBusinessId(null)}
          onSuccess={() => setInviteAdminBusinessId(null)}
        />
      )}

      {manageSeatsBusinessId && (
        <ManageSeatsModal
          businessId={manageSeatsBusinessId}
          onClose={() => setManageSeatsBusinessId(null)}
          onSuccess={async () => {
            setManageSeatsBusinessId(null);
            await fetch();
          }}
        />
      )}
    </div>
  );
}
